/**
 * Kurzmeldungen.
 *
 * Auf Radix Toast: Die Meldung landet in einer `aria-live`-Region, wird also
 * vorgelesen, ohne den Fokus zu stehlen (Kapitel 44). Eingeblendet wird **unten**,
 * über der Navigation — oben am Bildschirmrand wäre die Meldung bei einhändiger
 * Bedienung außer Reichweite und würde häufig übersehen.
 */
import * as RadixToast from "@radix-ui/react-toast";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { IconAlert, IconCheck, IconClose } from "@/components/icons";
import { t } from "@/i18n";
import { cn } from "@/lib/cn";

export type ToastTone = "neutral" | "success" | "warning" | "error";

export interface ToastRequest {
  title: string;
  body?: string;
  tone?: ToastTone;
  /** Anzeigedauer in Millisekunden. Fehlermeldungen bleiben länger stehen. */
  durationMs?: number;
}

interface ToastEntry extends ToastRequest {
  id: number;
}

interface ToastApi {
  notify: (request: ToastRequest) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Zugriff auf die Kurzmeldungen.
 *
 * Wirft absichtlich, wenn der Anbieter fehlt: Eine still verschluckte
 * Erfolgsmeldung wäre schlimmer als ein Fehler beim Entwickeln — der Nutzer
 * wüsste dann nicht, ob seine Meldung gespeichert wurde.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast benötigt einen umgebenden ToastProvider");
  return api;
}

const TONE_STYLES: Record<ToastTone, string> = {
  neutral: "border-line-strong bg-surface-raised text-ink",
  success: "border-status-green-fg bg-status-green-bg text-status-green-fg",
  warning: "border-status-yellow-fg bg-status-yellow-bg text-status-yellow-fg",
  error: "border-status-red-fg bg-status-red-bg text-status-red-fg",
};

const TONE_ICONS: Record<ToastTone, ReactNode> = {
  neutral: null,
  success: <IconCheck />,
  warning: <IconAlert />,
  error: <IconAlert />,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<ToastEntry[]>([]);

  const notify = useCallback((request: ToastRequest) => {
    setEntries((current) => [...current, { ...request, id: Date.now() + current.length }]);
  }, []);

  const api = useMemo<ToastApi>(() => ({ notify }), [notify]);

  const dismiss = useCallback((id: number) => {
    setEntries((current) => current.filter((entry) => entry.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={api}>
      <RadixToast.Provider swipeDirection="down" duration={5000}>
        {children}

        {entries.map((entry) => {
          const tone = entry.tone ?? "neutral";
          return (
            <RadixToast.Root
              key={entry.id}
              duration={entry.durationMs ?? (tone === "error" ? 9000 : 5000)}
              onOpenChange={(open) => {
                if (!open) dismiss(entry.id);
              }}
              className={cn(
                "flex items-start gap-3 rounded-card border px-3 py-3 shadow-raised",
                "data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)]",
                TONE_STYLES[tone],
              )}
            >
              {TONE_ICONS[tone] !== null && (
                <span className="mt-0.5 shrink-0">{TONE_ICONS[tone]}</span>
              )}
              <div className="min-w-0 flex-1">
                <RadixToast.Title className="text-[0.9375rem] font-semibold">
                  {entry.title}
                </RadixToast.Title>
                {entry.body !== undefined && (
                  <RadixToast.Description className="mt-0.5 text-[0.875rem] opacity-90">
                    {entry.body}
                  </RadixToast.Description>
                )}
              </div>
              <RadixToast.Close
                aria-label={t.common.close}
                // Volle Berührfläche auch hier: Eine Meldung, die man dreimal
                // antippen muss, um sie wegzubekommen, ist mit Handschuhen ein Ärgernis.
                className="-mt-1 -mr-1 flex size-touch shrink-0 items-center justify-center rounded-card opacity-70 hover:opacity-100"
              >
                <IconClose />
              </RadixToast.Close>
            </RadixToast.Root>
          );
        })}

        <RadixToast.Viewport
          className={cn(
            "fixed inset-x-0 z-[60] mx-auto flex w-full max-w-md list-none flex-col gap-2 px-3",
            // Über der unteren Navigation: 4,5 rem Leiste plus Sicherheitsabstand.
            "bottom-[calc(4.5rem+var(--safe-b))] md:bottom-4",
          )}
        />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}
